/**
 * Satellite map inset for IncidentDetail (PRD §5.8 S-04, v1.5).
 *
 * Mapbox GL JS is loaded LAZILY via CDN — on first click of "Show on
 * satellite map", a small loader injects the script + stylesheet, then
 * initialises the map.  Until the user clicks, the inset costs zero KB
 * of bundle and zero network requests.
 *
 * The component reads the Mapbox public token from
 * `import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN`. Three guard rails:
 *
 *   1. If the token is missing/empty: render an explainer fallback.
 *      The page still works; the satellite feature is just inactive.
 *   2. If the token doesn't start with "pk.": refuse to load. Mapbox
 *      "sk." tokens are SECRET and a leaked one is a billing risk.
 *      The check is belt-and-braces — Vite already won't expose
 *      anything not prefixed `VITE_*`.
 *   3. We never log or render the token value. Only a one-line
 *      "loaded" / "missing" status.
 *
 * The map shows:
 *   - Satellite imagery centred on the incident's segment GPS coords
 *   - Pulsing red incident pin at the centre
 *   - Zoom 17 fly-to animation
 *
 * Pipeline polyline + CCTV markers (BUILD acceptance #3) require corridor
 * GPS data on the API side — they're added as a follow-up once the
 * incident-detail response includes corridor anchor coordinates.
 * Documented in BUILD_v1.5_PROGRESS.md "Open Questions".
 */

import { useEffect, useRef, useState } from "react";
import { Satellite, X } from "lucide-react";

const MAPBOX_VERSION = "3.6.0";
const MAPBOX_JS_URL = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.js`;
const MAPBOX_CSS_URL = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.css`;

/** Public-token shape sanity check. EXPORTED for unit tests. */
export function isValidPublicToken(token: string | undefined): boolean {
  if (!token) return false;
  // Mapbox public tokens always start with "pk." and contain a JWT-like body.
  // Secret tokens start with "sk." — refusing those is the billing-risk
  // safeguard called out in BUILD_v1.5.md §Day 7 acceptance criteria.
  if (!token.startsWith("pk.")) return false;
  // Cheap structural check: pk.<payload>.<signature>
  return token.split(".").length === 3;
}

/**
 * Module-scoped promise so the script + stylesheet are only injected
 * once even if multiple SatelliteMapInset instances are mounted.
 */
let mapboxLoadPromise: Promise<MapboxNamespace> | null = null;

interface MapboxMarker {
  setLngLat: (lnglat: [number, number]) => MapboxMarker;
  addTo: (map: MapboxMapInstance) => MapboxMarker;
}

interface MapboxNamespace {
  Map: new (options: Record<string, unknown>) => MapboxMapInstance;
  Marker: new (options?: Record<string, unknown>) => MapboxMarker;
  accessToken: string;
}

interface MapboxMapInstance {
  remove: () => void;
  on: (event: string, handler: () => void) => void;
  addSource?: (id: string, source: Record<string, unknown>) => void;
  addLayer?: (layer: Record<string, unknown>) => void;
}

function loadMapboxGl(): Promise<MapboxNamespace> {
  if (mapboxLoadPromise) return mapboxLoadPromise;

  mapboxLoadPromise = new Promise<MapboxNamespace>((resolve, reject) => {
    // 1. Inject stylesheet (idempotent — guard with id)
    if (!document.getElementById("mapbox-gl-css")) {
      const link = document.createElement("link");
      link.id = "mapbox-gl-css";
      link.rel = "stylesheet";
      link.href = MAPBOX_CSS_URL;
      document.head.appendChild(link);
    }

    // 2. Inject script
    const existing = document.getElementById("mapbox-gl-js") as HTMLScriptElement | null;
    if (existing) {
      // Already loaded or loading; resolve when window.mapboxgl is ready
      const win = window as unknown as { mapboxgl?: MapboxNamespace };
      if (win.mapboxgl) {
        resolve(win.mapboxgl);
      } else {
        existing.addEventListener("load", () => {
          if (win.mapboxgl) resolve(win.mapboxgl);
          else reject(new Error("mapboxgl global undefined after script load"));
        });
        existing.addEventListener("error", () =>
          reject(new Error("Failed to load Mapbox GL JS from CDN")),
        );
      }
      return;
    }

    const script = document.createElement("script");
    script.id = "mapbox-gl-js";
    script.src = MAPBOX_JS_URL;
    script.async = true;
    script.onload = (): void => {
      const win = window as unknown as { mapboxgl?: MapboxNamespace };
      if (win.mapboxgl) resolve(win.mapboxgl);
      else reject(new Error("mapboxgl global undefined after script load"));
    };
    script.onerror = (): void =>
      reject(new Error("Failed to load Mapbox GL JS from CDN"));
    document.head.appendChild(script);
  });

  // Catch separately so we don't widen the resolved type to include `void`.
  // Resetting the cached promise on failure lets a later retry start fresh.
  mapboxLoadPromise.catch(() => {
    mapboxLoadPromise = null;
  });

  return mapboxLoadPromise;
}

interface SatelliteMapInsetProps {
  incidentId: string;
  segmentId: string;
  /** Segment centroid GPS — comes from the API response. */
  lat: number | null;
  lng: number | null;
  /**
   * Optional ordered list of corridor anchor coordinates `[lng, lat]`.
   * When provided, a gold dashed polyline is drawn over the satellite
   * imagery so the operator can see the pipeline route through the
   * incident location at a glance (PRD §5.8 S-04 acceptance).
   *
   * IMPORTANT: pass a memoised array (useMemo in the parent) — this
   * prop is in the map-init effect's dep list, so an unstable
   * reference will re-init the map on every parent render.
   */
  pipelineAnchors?: Array<[number, number]>;
  /**
   * Optional CCTV camera fixtures within visual range of the segment.
   * Drawn as small grey camera-icon markers. Same memoisation rule
   * as `pipelineAnchors`.
   */
  cctvMarkers?: Array<{ id: string; lng: number; lat: number; label?: string }>;
}

// Module-scoped stable references so that `cctvMarkers ?? EMPTY_CCTV` (and
// likewise for pipeline) doesn't create a new array per render when the
// caller omits the prop. The map-init effect depends on these arrays;
// stable identity prevents spurious teardown/rebuild cycles.
type CctvMarker = NonNullable<SatelliteMapInsetProps["cctvMarkers"]>[number];
const EMPTY_PIPELINE: Array<[number, number]> = [];
const EMPTY_CCTV: CctvMarker[] = [];

export function SatelliteMapInset(props: SatelliteMapInsetProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMapInstance | null>(null);

  const token = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN as string | undefined;
  const tokenValid = isValidPublicToken(token);

  // Stabilise array references — `props.pipelineAnchors ?? []` would
  // create a fresh empty array per render which the dep list would see
  // as a change. Reading via these constants gives identity stability.
  const pipelineAnchors = props.pipelineAnchors ?? EMPTY_PIPELINE;
  const cctvMarkers = props.cctvMarkers ?? EMPTY_CCTV;

  // Initialise the map ONLY when the user opens the panel.
  useEffect(() => {
    if (!open) return;
    if (!tokenValid) return;
    if (props.lat == null || props.lng == null) return;
    if (mapRef.current) return; // already initialised

    let cancelled = false;
    setLoading(true);
    setError(null);

    loadMapboxGl()
      .then((mapboxgl) => {
        if (cancelled) return;
        mapboxgl.accessToken = token!;

        if (!containerRef.current) {
          throw new Error("map container not in DOM");
        }
        const map = new mapboxgl.Map({
          container: containerRef.current,
          style: "mapbox://styles/mapbox/satellite-streets-v12",
          center: [props.lng!, props.lat!],
          zoom: 14,
          attributionControl: true,
        });
        mapRef.current = map;

        // Fly-to + drop a pulsing red pin once the map's ready.
        const mb = mapboxgl;
        map.on("load", () => {
          if (cancelled) return;
          // Add the incident pin as a Marker. Pulse via CSS animation
          // attached to the marker element class.
          const pinEl = document.createElement("div");
          pinEl.className = "sentinel-mapbox-pin";
          new mb.Marker({ element: pinEl })
            .setLngLat([props.lng!, props.lat!])
            .addTo(map);

          // Pipeline polyline overlay (S-04 acceptance #3, Day 10).
          // We draw the pipeline as a gold dashed line so the operator
          // can see the corridor passing through the incident pin even
          // before zooming out. Drawn before the marker so the marker
          // visually layers above the line.
          if (pipelineAnchors.length >= 2) {
            try {
              map.addSource?.("sentinel-pipeline", {
                type: "geojson",
                data: {
                  type: "Feature",
                  properties: {},
                  geometry: {
                    type: "LineString",
                    coordinates: pipelineAnchors,
                  },
                },
              });
              map.addLayer?.({
                id: "sentinel-pipeline-line",
                type: "line",
                source: "sentinel-pipeline",
                layout: { "line-join": "round", "line-cap": "round" },
                paint: {
                  "line-color": "#fbbf24",
                  "line-width": 3,
                  "line-dasharray": [3, 2],
                  "line-opacity": 0.9,
                },
              });
            } catch {
              /* mapbox not ready or layer already exists — non-fatal */
            }
          }

          // CCTV camera markers (BUILD §S-04 acceptance #4). Each renders
          // as a small grey square so they're visually distinct from the
          // red pulsing incident pin. No-op when the list is empty.
          for (const cam of cctvMarkers) {
            const camEl = document.createElement("div");
            camEl.className = "sentinel-mapbox-cctv";
            camEl.title = cam.label ?? cam.id;
            camEl.setAttribute("data-cctv-id", cam.id);
            new mb.Marker({ element: camEl })
              .setLngLat([cam.lng, cam.lat])
              .addTo(map);
          }

          // Fly to incident location at zoom 17 per BUILD §Day 7 spec.
          (map as unknown as { flyTo: (opts: Record<string, unknown>) => void }).flyTo(
            {
              center: [props.lng!, props.lat!],
              zoom: 17,
              speed: 0.8,
              curve: 1.4,
              essential: true,
            },
          );
          setLoading(false);
        });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // Re-init on `open` toggles AND when the overlay inputs change
    // identity (callers must memoise pipelineAnchors / cctvMarkers, see
    // the prop docstrings). lat/lng are intentionally read fresh inside
    // the effect body, so they're included to keep ESLint sound; in
    // practice the parent re-keys the component when the incident
    // changes so this just hardens against future direct prop swaps.
  }, [open, tokenValid, props.lat, props.lng, token, pipelineAnchors, cctvMarkers]);

  // ── Fallback states (rendered as the inset section) ──────────────

  if (!tokenValid) {
    return (
      <section
        data-testid="satellite-map-inset"
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-xs"
      >
        <div className="flex items-center gap-2 text-slate-400">
          <Satellite className="size-4" />
          <span className="font-semibold uppercase tracking-wide">
            Satellite view
          </span>
        </div>
        <div className="mt-2 text-slate-500" data-testid="satellite-map-fallback">
          Satellite view unavailable — Mapbox token not configured.
          Set <code className="text-slate-300">VITE_MAPBOX_PUBLIC_TOKEN</code>
          {" "}in <code className="text-slate-300">apps/sentinel-console/.env</code>
          {" "}and rebuild to enable.
        </div>
      </section>
    );
  }

  if (props.lat == null || props.lng == null) {
    return (
      <section
        data-testid="satellite-map-inset"
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-xs text-slate-500"
      >
        <div className="flex items-center gap-2 text-slate-400">
          <Satellite className="size-4" />
          <span className="font-semibold uppercase tracking-wide text-slate-300">
            Satellite view
          </span>
        </div>
        <div className="mt-2" data-testid="satellite-map-no-coords">
          No GPS coordinates available for segment {props.segmentId}.
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="satellite-map-inset"
      className="rounded-lg border border-slate-800 bg-slate-900/40 overflow-hidden"
    >
      <header className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-800">
        <div className="flex items-center gap-2 text-xs text-slate-300">
          <Satellite className="size-3.5 text-teal-400" />
          <span className="font-semibold uppercase tracking-wide">Satellite view</span>
          <span className="text-slate-500 font-mono normal-case">
            {props.segmentId} · {props.lat.toFixed(4)}, {props.lng.toFixed(4)}
          </span>
        </div>
        {open ? (
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="satellite-map-close"
            className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
          >
            <X className="size-3.5" /> Close
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="satellite-map-open"
            className="text-xs bg-teal-500/20 text-teal-200 border border-teal-700 rounded px-2 py-1 hover:bg-teal-500/30"
          >
            Show on satellite map
          </button>
        )}
      </header>
      {open && (
        <div className="relative">
          {loading && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-slate-950/80 text-xs text-slate-300 z-10"
              data-testid="satellite-map-loading"
            >
              Loading satellite imagery…
            </div>
          )}
          {error && (
            <div
              className="px-4 py-3 text-xs text-rose-300 bg-rose-950/30"
              data-testid="satellite-map-error"
            >
              {error}
            </div>
          )}
          <div
            ref={containerRef}
            data-testid="satellite-map-canvas"
            style={{ width: "100%", height: 360 }}
            aria-label={`Satellite map centred on ${props.segmentId}`}
          />
        </div>
      )}
    </section>
  );
}
